import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api } from '@/services/client';
import { toast } from 'sonner';

export const WS_SESSION_STORAGE_KEY = 'khiimori_workspace_session_id';

export interface SessionInfo {
  id: string;
  name: string;
  description?: string | null;
  icon?: string | null;
  color?: string | null;
  tab_count: number;
  total_time_seconds?: number;
  created_at: string;
  is_active: boolean;
}

export interface SessionListRow {
  id: string;
  name: string;
  is_active: boolean;
  tab_count: number;
}

export interface TabMonitorSessionContextValue {
  sessionInfo: SessionInfo | null;
  workspaceSessionId: string | null;
  workspaceOptions: SessionListRow[];
  isLoading: boolean;
  refreshWorkspaceList: () => Promise<void>;
  handleWorkspaceChange: (newId: string) => Promise<void>;
  /** Updates active workspace id and session banner after server-side session correction (e.g. 403 retry). */
  refreshSessionById: (id: string) => Promise<void>;
}

const TabMonitorSessionContext = createContext<TabMonitorSessionContextValue | null>(null);

export function TabMonitorSessionProvider({ children }: { children: ReactNode }) {
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  const [workspaceSessionId, setWorkspaceSessionId] = useState<string | null>(null);
  const [workspaceOptions, setWorkspaceOptions] = useState<SessionListRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const refreshWorkspaceList = useCallback(async () => {
    try {
      const res = await api.get<{ sessions: SessionListRow[] }>('/sessions');
      setWorkspaceOptions(res.data.sessions ?? []);
    } catch (e) {
      console.error('Failed to list workspaces:', e);
    }
  }, []);

  const handleWorkspaceChange = useCallback(
    async (newId: string) => {
      if (!newId || newId === workspaceSessionId) return;
      const previousId = workspaceSessionId;
      try {
        await api.put(`/sessions/${newId}`, { is_active: true });
        sessionStorage.setItem(WS_SESSION_STORAGE_KEY, newId);
        const detail = await api.get<SessionInfo>(`/sessions/${newId}`);
        setSessionInfo(detail.data);
        setWorkspaceSessionId(newId);
        await refreshWorkspaceList();
        toast.success(`Switched to ${detail.data.name}`);
      } catch (e) {
        console.error('Failed to switch workspace:', e);
        // If we got far enough to change storage but the GET failed, revert
        if (previousId) {
          setWorkspaceSessionId(previousId);
        }
        toast.error(e instanceof Error ? e.message : 'Failed to switch workspace');
      }
    },
    [workspaceSessionId, refreshWorkspaceList]
  );

  const refreshSessionById = useCallback(async (id: string) => {
    if (!id) return;
    try {
      sessionStorage.setItem(WS_SESSION_STORAGE_KEY, id);
      const detail = await api.get<SessionInfo>(`/sessions/${id}`);
      setSessionInfo(detail.data);
      setWorkspaceSessionId(id);
      await refreshWorkspaceList();
    } catch (e) {
      console.error('Failed to refresh session by id:', e);
      setWorkspaceSessionId(id);
    }
  }, [refreshWorkspaceList]);

  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      setIsLoading(true);
      try {
        const cur = await api.get<{ session: SessionInfo }>('/sessions/current');
        if (cancelled) return;
        const sessionData = cur.data.session;
        if (!sessionData?.id) {
          toast.error('No workspace session found. Create one in Tab Monitor.');
          return;
        }
        setSessionInfo(sessionData);
        const cached = sessionStorage.getItem(WS_SESSION_STORAGE_KEY);
        if (cached !== sessionData.id) {
          sessionStorage.setItem(WS_SESSION_STORAGE_KEY, sessionData.id);
        }
        setWorkspaceSessionId(sessionData.id);
        await refreshWorkspaceList();
      } catch (e) {
        if (!cancelled) {
          console.error('Failed to resolve workspace session:', e);
          toast.error(e instanceof Error ? e.message : 'Failed to connect to workspace session');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    void boot();
    return () => {
      cancelled = true;
    };
  }, [refreshWorkspaceList]);

  const value = useMemo<TabMonitorSessionContextValue>(
    () => ({
      sessionInfo,
      workspaceSessionId,
      workspaceOptions,
      isLoading,
      refreshWorkspaceList,
      handleWorkspaceChange,
      refreshSessionById,
    }),
    [
      sessionInfo,
      workspaceSessionId,
      workspaceOptions,
      isLoading,
      refreshWorkspaceList,
      handleWorkspaceChange,
      refreshSessionById,
    ]
  );

  return (
    <TabMonitorSessionContext.Provider value={value}>{children}</TabMonitorSessionContext.Provider>
  );
}

export function useTabMonitorSession(): TabMonitorSessionContextValue {
  const ctx = useContext(TabMonitorSessionContext);
  if (!ctx) {
    throw new Error('useTabMonitorSession must be used within TabMonitorSessionProvider');
  }
  return ctx;
}

/** Returns null when rendered outside Tab Monitor layout (e.g. legacy standalone routes). */
export function useTabMonitorSessionOptional(): TabMonitorSessionContextValue | null {
  return useContext(TabMonitorSessionContext);
}
